// Directional trend (rate of change) for a tank parameter, computed from
// its readings. This is deliberately distinct from the off-band STATUS
// (ok / high / low): a value can sit inside the band while steadily
// falling toward the floor — exactly the signal the recommendation copy
// was blind to when it only looked at the latest number.
//
// Pure + deterministic (no AI). A least-squares slope over a recent
// window gives units-per-day; a per-param "flat" threshold keeps probe
// noise from reading as a real trend. Dense HYDROS ingest (15-min) makes
// the slope robust; on sparse manual tests it simply returns null until
// there are enough points.

// Per-param "flat" band in units-per-day. A |rate| below this is treated
// as stable (normal jitter / probe noise), so we never cry "falling" at
// noise. Tuned to roughly the daily drift a reefer would shrug at.
const FLAT_EPS = {
  temp: 0.6, // °F/day
  ph: 0.04, // pH/day
  salinity: 0.3, // ppt/day
  alk: 0.2, // dKH/day
  calcium: 6, // ppm/day
  magnesium: 12, // ppm/day
  nitrate: 1.5, // ppm/day
  phosphate: 0.012, // ppm/day
};

// readings: [{ value, ts }] in any order. Returns
//   { direction: 'rising'|'falling'|'flat', ratePerDay, n, spanHours }
// or null when there isn't enough spread to trust a slope.
export function computeTrend(
  readings,
  paramKey,
  { windowHours = 72, minPoints = 4, now = Date.now() } = {}
) {
  if (!Array.isArray(readings) || readings.length < minPoints) return null;
  const cutoff = now - windowHours * 3600 * 1000;
  const pts = readings
    .map((r) => ({ t: new Date(r.ts).getTime(), v: Number(r.value) }))
    .filter((p) => Number.isFinite(p.t) && Number.isFinite(p.v) && p.t >= cutoff)
    .sort((a, b) => a.t - b.t);
  if (pts.length < minPoints) return null;
  const spanHours = (pts[pts.length - 1].t - pts[0].t) / 3_600_000;
  // Need a real time spread — a cluster of points in five minutes can't
  // anchor a per-day rate.
  if (spanHours < 2) return null;

  // Least-squares slope of value over time-in-days.
  const xs = pts.map((p) => p.t / 86_400_000);
  const ys = pts.map((p) => p.v);
  const n = xs.length;
  const mx = xs.reduce((a, b) => a + b, 0) / n;
  const my = ys.reduce((a, b) => a + b, 0) / n;
  let num = 0;
  let den = 0;
  for (let i = 0; i < n; i++) {
    num += (xs[i] - mx) * (ys[i] - my);
    den += (xs[i] - mx) ** 2;
  }
  if (den === 0) return null;
  const ratePerDay = num / den;
  const eps = FLAT_EPS[paramKey] ?? 0;
  let direction = 'flat';
  if (ratePerDay > eps) direction = 'rising';
  else if (ratePerDay < -eps) direction = 'falling';
  return { direction, ratePerDay, n, spanHours };
}

// Round a rate for display: whole numbers for big-scale params (Ca/Mg),
// one decimal otherwise.
export function formatRate(ratePerDay) {
  const mag = Math.abs(ratePerDay);
  return mag >= 10 ? String(Math.round(mag)) : String(Math.round(mag * 10) / 10);
}

// Compact badge for a param card, e.g. "↓ 0.3/day". null when flat/unknown
// so the card stays clean when there's nothing to say.
export function trendBadge(trend) {
  if (!trend || trend.direction === 'flat') return null;
  const arrow = trend.direction === 'rising' ? '↑' : '↓';
  return `${arrow} ${formatRate(trend.ratePerDay)}/day`;
}

// A rate in the keeper's units with its unit, e.g. "0.3 dKH/day",
// "1 °C/day", "0.0004 S.G./day". The slope is computed on stored values
// (°F, ppt), so a Celsius or S.G. keeper's rate is converted here: a bare
// "~1.8/day" on a °C tank was °F per day with no unit. pH and phosphate
// keep the places their kits read, so a pH slide never rounds to "0.1".
// `opts.unit` is the param's stored unit label; `tempUnit` 'F' | 'C';
// `salinityUnit` 'ppt' | 'psu' | 'sg'.
export function formatTrendRate(paramKey, ratePerDay, opts = {}) {
  let mag = Math.abs(Number(ratePerDay));
  if (!Number.isFinite(mag)) return '';
  let unit = opts.unit || '';
  let decimals = mag >= 10 ? 0 : 1;
  if (paramKey === 'temp') {
    if (opts.tempUnit === 'C') {
      mag = (mag * 5) / 9;
      unit = '°C';
    } else {
      unit = '°F';
    }
    decimals = 1;
  } else if (paramKey === 'salinity') {
    if (opts.salinityUnit === 'sg') {
      mag = (mag * 0.026) / 35;
      unit = 'S.G.';
      decimals = 4;
    } else {
      unit = opts.salinityUnit === 'psu' ? 'PSU' : 'ppt';
      decimals = 1;
    }
  } else if (paramKey === 'ph') {
    unit = 'pH';
    decimals = 2;
  } else if (paramKey === 'phosphate') {
    decimals = 3;
  }
  const n = Number(mag.toFixed(decimals)).toString();
  return unit ? `${n} ${unit}/day` : `${n}/day`;
}

// One trend-aware clause to append to recommendation copy, given the
// off-band status. Returns '' when there's nothing useful to add (flat,
// or a trend that agrees with the obvious fix). The value is the keeper-
// facing nuance: "in-band but sliding toward the floor", or "already
// recovering, don't overcorrect".
export function trendClause(paramKey, status, trend, units = {}) {
  if (!trend || trend.direction === 'flat') return '';
  const rate = formatTrendRate(paramKey, trend.ratePerDay, units);
  if (status === 'low') {
    return trend.direction === 'falling'
      ? ` It's also still falling (~${rate}), so correct toward the upper half of the band, not just over the line.`
      : ` It's already climbing back (~${rate}) — recheck before adding more so you don't overshoot.`;
  }
  if (status === 'high') {
    return trend.direction === 'rising'
      ? ` And it's still rising (~${rate}) — find what's pushing it up before it climbs further.`
      : ` It's already easing down (~${rate}); give it time before intervening.`;
  }
  // In-band ('ok') but drifting toward an edge is the signal the old copy
  // missed entirely.
  return ` Heads up: it's in range but ${trend.direction} ~${rate} — worth watching.`;
}
